<template>
  <div
    class="support-widget bg-white/[0.03] border border-white/10"
    :class="compact ? 'rounded-xl p-4' : 'rounded-2xl p-6 md:p-8'"
  >
    <!-- Mode tabs -->
    <div
      class="flex gap-1 bg-black/30 rounded-full"
      :class="compact ? 'p-0.5 mb-4' : 'p-1 mb-6 max-w-sm'"
    >
      <button
        type="button"
        class="flex-1 font-bold uppercase tracking-widest rounded-full transition"
        :class="[
          mode === 'donation' ? 'bg-primary text-black' : 'text-text-muted hover:text-white',
          compact ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-2 text-xs'
        ]"
        @click="mode = 'donation'"
      >
        {{ compact ? $t('artist.support.tab_tip_short') : $t('artist.support.tab_tip') }}
      </button>
      <button
        type="button"
        class="flex-1 font-bold uppercase tracking-widest rounded-full transition"
        :class="[
          mode === 'support' ? 'bg-primary text-black' : 'text-text-muted hover:text-white',
          compact ? 'px-2 py-1.5 text-[10px]' : 'px-4 py-2 text-xs'
        ]"
        @click="mode = 'support'"
      >
        {{ compact ? $t('artist.support.tab_monthly_short') : $t('artist.support.tab_monthly') }}
      </button>
    </div>

    <h3
      class="font-bold font-heading uppercase tracking-wide mb-1"
      :class="compact ? 'text-base' : 'text-2xl'"
    >
      {{ mode === 'support'
        ? (compact
            ? $t('artist.support.title_support_short', { name: artistName })
            : $t('artist.support.title_support', { name: artistName }))
        : $t('artist.support.title_tip', { name: artistName }) }}
    </h3>
    <p
      v-if="!compact"
      class="text-sm text-text-muted mb-6 leading-relaxed"
    >
      {{ mode === 'support'
        ? $t('artist.support.sub_support')
        : $t('artist.support.sub_tip') }}
    </p>
    <div v-else class="mb-4"></div>

    <!-- Logged-in identity strip — replaces the email/name fields when
         we already know who you are. Surfaces the balance + a toggle to
         pay from it on the donation tab. -->
    <div
      v-if="ctx?.loggedIn"
      class="flex items-center gap-3 mb-5 px-4 py-3 rounded-xl bg-primary/5 border border-primary/15"
    >
      <NuxtIcon name="mdi:account-check" size="20" class="text-primary flex-shrink-0" />
      <div class="flex-1 min-w-0">
        <div class="text-xs text-text-muted uppercase tracking-widest font-bold">{{ $t('artist.support.signed_in_as') }}</div>
        <div class="text-sm text-white font-semibold truncate">{{ ctx.email }}</div>
      </div>
      <div v-if="ctx.canPayFromBalance" class="text-right">
        <div class="text-[10px] text-text-muted uppercase tracking-widest font-bold">{{ $t('artist.support.balance') }}</div>
        <div class="text-sm font-mono font-bold text-primary">€{{ (ctx.balanceCents / 100).toFixed(2) }}</div>
      </div>
    </div>

    <!-- Funding source toggle — visible whenever we can pay from
         balance. Subscriptions debit the FIRST month immediately and
         the renewal cron handles each subsequent month; the user can
         cancel anytime, and `past_due` status protects the artist
         from a "supporter ran out of balance" silently. Public/cross-
         tenant viewers don't get the toggle (canPayFromBalance is
         false in that case). -->
    <div
      v-if="ctx?.canPayFromBalance"
      class="flex gap-2 mb-5"
    >
      <button
        type="button"
        class="flex-1 px-3 py-2.5 rounded-lg border text-xs font-bold uppercase tracking-widest transition"
        :class="paymentSource === 'balance'
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-white/10 text-text-muted hover:border-white/30 hover:text-white'"
        :disabled="effectiveCents > ctx.balanceCents"
        :title="effectiveCents > ctx.balanceCents ? $t('artist.support.exceeds_balance_short') : ''"
        @click="paymentSource = 'balance'"
      >
        <NuxtIcon name="mdi:wallet" size="14" class="inline -mt-0.5 mr-1" />
        {{ $t('artist.support.pay_from_balance') }}
      </button>
      <button
        type="button"
        class="flex-1 px-3 py-2.5 rounded-lg border text-xs font-bold uppercase tracking-widest transition"
        :class="paymentSource === 'stripe'
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-white/10 text-text-muted hover:border-white/30 hover:text-white'"
        @click="paymentSource = 'stripe'"
      >
        <NuxtIcon name="mdi:credit-card-outline" size="14" class="inline -mt-0.5 mr-1" />
        {{ $t('artist.support.pay_card') }}
      </button>
    </div>
    <p
      v-if="ctx?.canPayFromBalance && paymentSource === 'balance' && effectiveCents > ctx.balanceCents"
      class="text-[11px] text-amber-400 mb-4 -mt-2"
    >
      {{ $t('artist.support.exceeds_balance') }}
    </p>
    <p
      v-if="ctx?.canPayFromBalance && paymentSource === 'balance' && mode === 'support'"
      class="text-[11px] text-text-muted mb-4 -mt-2 leading-relaxed"
    >
      {{ $t('artist.support.balance_renew_note') }}
    </p>

    <!-- Amount presets -->
    <label class="block text-xs font-bold uppercase tracking-widest text-text-muted mb-2">
      {{ mode === 'support' ? $t('artist.support.amount_monthly') : $t('artist.support.amount') }}
    </label>
    <div
      class="grid gap-2 mb-3"
      :class="compact ? 'grid-cols-2' : 'grid-cols-4'"
    >
      <button
        v-for="preset in presets"
        :key="preset"
        type="button"
        class="py-2 text-sm font-bold rounded-lg border transition"
        :class="amountCents === preset * 100 && !customMode
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-white/10 text-text-muted hover:border-white/30 hover:text-white'"
        @click="selectPreset(preset)"
      >
        €{{ preset }}{{ mode === 'support' ? $t('artist.per_month_short') : '' }}
      </button>
    </div>
    <div class="flex items-center gap-2 mb-6">
      <label class="text-xs text-text-muted whitespace-nowrap">{{ $t('artist.support.custom') }}</label>
      <div class="flex-1 flex items-center bg-black/30 border border-white/10 rounded-lg pl-3 focus-within:border-primary/50">
        <span class="text-text-muted text-sm">€</span>
        <input
          v-model.number="customAmount"
          @focus="customMode = true"
          type="number"
          :min="(min / 100)"
          :max="(max / 100)"
          step="0.5"
          class="flex-1 bg-transparent border-none outline-none px-2 py-2 text-white font-semibold text-base"
          placeholder="0.00"
        />
      </div>
    </div>

    <!-- Email — hidden when the viewer is already logged in (we know it). -->
    <template v-if="!ctx?.loggedIn">
      <label class="block text-xs font-bold uppercase tracking-widest text-text-muted mb-2">{{ $t('artist.support.your_email') }}</label>
      <input
        v-model="email"
        type="email"
        :placeholder="$t('artist.support.email_placeholder')"
        class="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 mb-4 text-white text-sm focus:border-primary/60 outline-none transition"
        required
        maxlength="200"
      />
    </template>

    <!-- Display name -->
    <label class="block text-xs font-bold uppercase tracking-widest text-text-muted mb-2">{{ $t('artist.support.display_name') }}</label>
    <input
      v-model="displayName"
      type="text"
      :placeholder="$t('artist.support.anonymous')"
      class="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 mb-4 text-white text-sm focus:border-primary/60 outline-none transition"
      maxlength="60"
    />

    <!-- Message -->
    <label class="block text-xs font-bold uppercase tracking-widest text-text-muted mb-2">{{ $t('artist.support.message_label') }}</label>
    <textarea
      v-model="message"
      :placeholder="$t('artist.support.message_placeholder')"
      rows="3"
      class="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 mb-4 text-white text-sm focus:border-primary/60 outline-none transition resize-none"
      maxlength="500"
    ></textarea>
    <div class="text-[10px] text-text-muted text-right mb-4 font-mono">{{ message.length }}/500</div>

    <!-- Public toggle -->
    <label class="flex items-center gap-3 mb-6 cursor-pointer select-none group">
      <input v-model="isPublic" type="checkbox" class="accent-primary w-4 h-4 cursor-pointer" />
      <span class="text-sm text-text-muted group-hover:text-white transition">
        {{ $t('artist.support.show_name') }}
      </span>
    </label>

    <!-- Submit -->
    <button
      type="button"
      class="w-full rounded-xl bg-primary text-black font-bold uppercase tracking-widest hover:brightness-110 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition"
      :class="compact ? 'py-3 text-sm' : 'py-4 text-base'"
      :disabled="!canSubmit || submitting"
      @click="submit"
    >
      <span v-if="submitting" class="inline-flex items-center gap-2">
        <span class="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></span>
        {{ paymentSource === 'balance' ? $t('artist.support.processing') : $t('artist.support.redirecting') }}
      </span>
      <span v-else-if="mode === 'support'">
        {{ $t('artist.support.submit_support', { amount: amountEur.toFixed(2) }) }}
      </span>
      <span v-else-if="paymentSource === 'balance'">
        {{ $t('artist.support.submit_balance', { amount: amountEur.toFixed(2) }) }}
      </span>
      <span v-else>
        {{ $t('artist.support.submit_tip', { amount: amountEur.toFixed(2) }) }}
      </span>
    </button>

    <p class="text-[11px] text-text-muted text-center mt-4 leading-relaxed">
      <template v-if="paymentSource === 'balance'">
        {{ $t('artist.support.note_balance', { name: artistName }) }}
      </template>
      <template v-else>
        {{ mode === 'support'
          ? $t('artist.support.note_stripe_support')
          : $t('artist.support.note_stripe_tip') }}
      </template>
    </p>

    <!-- Who the buyer is actually contracting with. Diztree takes the charge
         and owes the VAT on it, so § 5 TMG and the withdrawal notice name
         Diztree — `?merchant=1` makes those pages serve the seller's texts
         rather than the storefront's. -->
    <p class="text-[11px] text-text-muted text-center mt-2 leading-relaxed">
      {{ $t('artist.support.sold_by', 'Sold by Diztree.') }}
      <NuxtLink to="/terms?merchant=1" target="_blank" class="underline">{{ $t('terms.title') }}</NuxtLink>
      ·
      <NuxtLink to="/widerrufsrecht?merchant=1" target="_blank" class="underline">{{ $t('shop.withdrawal_policy', 'Right of withdrawal') }}</NuxtLink>
      ·
      <NuxtLink to="/imprint?merchant=1" target="_blank" class="underline">{{ $t('imprint.title') }}</NuxtLink>
    </p>

    <!-- Success state (balance path only — Stripe redirects away). -->
    <div
      v-if="successMessage"
      class="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-semibold flex items-center gap-3"
    >
      <NuxtIcon name="mdi:check-circle" size="20" class="flex-shrink-0" />
      <div>{{ successMessage }}</div>
    </div>

    <p v-if="errorMessage" class="text-xs text-red-400 mt-3 text-center font-semibold">{{ errorMessage }}</p>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'

const { t, locale } = useI18n()

const props = withDefaults(defineProps<{
    slug: string
    artistName: string
    // Sidebar / narrow-column rendering: tighter paddings, shorter
    // copy, 2-col preset grid. Keep `false` for full-width usages
    // (admin/staff preview, team/artists/[id].vue).
    compact?: boolean
}>(), {
    compact: false
})

const emit = defineEmits<{
    (e: 'donated'): void
}>()

const mode = ref<'donation' | 'support'>('donation')
const presets = computed(() => mode.value === 'support' ? [3, 5, 10, 25] : [3, 5, 10, 25])
const customMode = ref(false)
const customAmount = ref<number | null>(null)
const amountCents = ref<number>(500) // €5 default

const min = computed(() => 100)   // €1.00
const max = computed(() => mode.value === 'support' ? 100_000 : 1_000_000)

const email = ref('')
const displayName = ref('')
const message = ref('')
const isPublic = ref(false)
const paymentSource = ref<'stripe' | 'balance'>('stripe')

const submitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

// Viewer context — logged-in identity + balance + same-tenant flag.
// One round-trip on mount; the server is also the one source of truth
// for `canPayFromBalance` (it gates on currency + cross-tenant), which
// keeps the UI from offering a path the server would refuse.
interface SupportContext {
    loggedIn: boolean
    email?: string
    displayName?: string
    balanceCents: number
    currency: string
    canPayFromBalance: boolean
    sameTenant?: boolean
}

// SSR-resolve the viewer context so the page renders in the correct
// mode (logged-in vs. guest) on first paint. Previously this was a
// client-only fetch in onMounted, which produced a visible flash:
// the SSR DOM had the email field, then after hydration the field
// vanished for logged-in users. useAsyncData waits during SSR so the
// initial HTML already reflects the resolved state.
const { data: ctxData } = await useAsyncData(
    () => `support-ctx:${props.slug}`,
    () => $fetch<SupportContext>('/api/public/artist/support-context', {
        query: { slug: props.slug }
    }).catch(() => ({ loggedIn: false, balanceCents: 0, currency: 'EUR', canPayFromBalance: false } as SupportContext))
)
const ctx = computed<SupportContext | null>(() => ctxData.value || null)

onMounted(() => {
    const data = ctx.value
    if (data?.loggedIn) {
        if (data.email) email.value = data.email
        if (data.displayName && !displayName.value) displayName.value = data.displayName
        // Default to balance funding when it's available and the
        // viewer has enough for the €5 preset; otherwise stay on
        // Stripe so the button doesn't start disabled.
        if (data.canPayFromBalance && data.balanceCents >= amountCents.value) {
            paymentSource.value = 'balance'
        }
    }
})

const amountEur = computed(() => {
    if (customMode.value && customAmount.value) {
        return Math.max(1, Number(customAmount.value) || 0)
    }
    return amountCents.value / 100
})

const effectiveCents = computed(() => {
    if (customMode.value && customAmount.value) {
        return Math.round(Math.max(1, Number(customAmount.value) || 0) * 100)
    }
    return amountCents.value
})

const canSubmit = computed(() => {
    const cents = effectiveCents.value
    if (!Number.isFinite(cents) || cents < min.value || cents > max.value) return false
    // Stripe path needs an email (logged-in users have it pre-filled).
    if (paymentSource.value === 'stripe') {
        if (!email.value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) return false
    }
    // Balance path needs the viewer to be logged in with enough cents.
    if (paymentSource.value === 'balance') {
        if (!ctx.value?.canPayFromBalance) return false
        if (cents > (ctx.value?.balanceCents || 0)) return false
    }
    return true
})

const selectPreset = (preset: number) => {
    customMode.value = false
    customAmount.value = null
    amountCents.value = preset * 100
}

// Reset to safe defaults when switching tabs — donation custom values
// can push past the support max otherwise, and balance funding doesn't
// apply to subscriptions. Also clear the free-text fields so a message
// like "Thanks for the tip!" doesn't accidentally ride on a monthly
// subscription the user just toggled to.
watch(mode, () => {
    customMode.value = false
    customAmount.value = null
    amountCents.value = 500
    errorMessage.value = ''
    successMessage.value = ''
    message.value = ''
    // Don't force paymentSource — subscriptions now support balance
    // funding (internal-staff flow), so keep the viewer's choice
    // across the tab switch.
})

// Auto-fallback: if the chosen amount goes past balance, swing back to
// Stripe so the user isn't stuck with a disabled button they can't
// debug. They can re-toggle once they lower the amount.
watch(effectiveCents, (cents) => {
    if (
        paymentSource.value === 'balance'
        && ctx.value?.canPayFromBalance
        && cents > (ctx.value?.balanceCents || 0)
    ) {
        // Stay on balance so the inline warning shows; don't auto-flip
        // away because that hides the fact they're over budget.
    }
})

// Per-form-instance idempotency nonce — generated once when the
// widget mounts and resent with every submission attempt. If the
// network connection drops between request and response (the
// classic "user clicks twice because spinner didn't appear yet"
// scenario), the server collapses the duplicate via the unique
// index on (tenant, clientNonce) instead of creating a second
// debit / Stripe session.
// `submissionNonce` MUST be a ref so we can rotate it after each
// terminal outcome — a single sticky nonce per widget instance meant
// the second balance-funded tip in the same session collided with the
// first via E11000 and replayed instead of creating a new donation.
// Rule: generate a fresh nonce on mount AND after every submission
// settles (success OR failure), so the next attempt is a brand-new
// idempotency key.
const makeNonce = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID().replace(/-/g, '')
    : Math.random().toString(36).slice(2) + Date.now().toString(36)
const submissionNonce = ref(makeNonce())

const submit = async () => {
    if (!canSubmit.value) return
    submitting.value = true
    errorMessage.value = ''
    successMessage.value = ''
    try {
        const res = await $fetch<{ url?: string; paid?: boolean; donationId?: string; supportId?: string; balanceAfterCents?: number; renewsAt?: string }>(
            '/api/public/artist/contribute',
            {
                method: 'POST',
                body: {
                    slug: props.slug,
                    kind: mode.value,
                    amountCents: effectiveCents.value,
                    email: email.value.trim() || undefined,
                    displayName: displayName.value.trim() || undefined,
                    message: message.value.trim() || undefined,
                    isPublic: isPublic.value,
                    paymentSource: paymentSource.value,
                    clientNonce: submissionNonce.value
                }
            }
        )

        // Balance path returns synchronously — donation OR subscription.
        if (res?.paid) {
            if (mode.value === 'support') {
                const renewsAt = res.renewsAt ? new Date(res.renewsAt) : null
                const renewsLabel = renewsAt
                    ? renewsAt.toLocaleDateString(locale.value || undefined, { day: '2-digit', month: 'short' })
                    : t('artist.support.next_month')
                successMessage.value = t('artist.support.success_subscription', {
                    amount: amountEur.value.toFixed(2),
                    renews: renewsLabel
                })
            } else {
                successMessage.value = t('artist.support.success_tip', {
                    amount: amountEur.value.toFixed(2),
                    name: props.artistName
                })
            }
            if (ctx.value && typeof res.balanceAfterCents === 'number') {
                ctx.value.balanceCents = res.balanceAfterCents
                ctx.value.canPayFromBalance = res.balanceAfterCents > 0
            }
            // Reset for a follow-up tip — including a fresh nonce so
            // the next submission is a NEW donation, not an idempotent
            // replay of this one.
            message.value = ''
            submitting.value = false
            submissionNonce.value = makeNonce()
            emit('donated')
            return
        }

        // Stripe path: hand off to checkout URL.
        if (res?.url) {
            window.location.href = res.url
            return
        }

        errorMessage.value = t('artist.support.err_checkout')
        submitting.value = false
        submissionNonce.value = makeNonce()
    } catch (err: any) {
        console.error('[SupportWidget] submit failed', err)
        errorMessage.value = err?.data?.statusMessage || err?.statusMessage || err?.message || t('artist.support.err_generic')
        submitting.value = false
        // Rotate the nonce after a failed submission too — a transient
        // server error (network blip, Stripe outage) shouldn't lock
        // the form into replaying the failed attempt. The next click
        // gets a fresh idempotency key and a real new request.
        submissionNonce.value = makeNonce()
    }
}
</script>
